import React, { useState, useEffect } from 'react';
import { 
  Radio, 
  Button, 
  Space, 
  Card, 
  Typography, 
  Alert,
  Spin,
  Empty,
  Input,
  Tag,
  message,
  Popconfirm
} from 'antd';
import { 
  YoutubeOutlined, 
  PlusOutlined, 
  SearchOutlined,
  CheckCircleOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import type { RadioChangeEvent } from 'antd';
import { authAxios } from '../utils/session_utils';
import { YouTubeOAuthToken } from './types';
import { makeKeyInLowerCaseHelper } from '../utils';

const { Title, Text } = Typography;

interface ChannelSelectorProps {
  streamId: string;
  userId: string;
  selectedTokenId?: string;
  onTokenSelected: (tokenId: string, channelInfo: { id: string; title: string }) => void;
  onConnectNew: () => void;
  refreshTrigger?: number;
}

const ChannelSelector: React.FC<ChannelSelectorProps> = ({
  streamId,
  userId,
  selectedTokenId,
  onTokenSelected,
  onConnectNew,
  refreshTrigger
}) => {
  const [tokens, setTokens] = useState<YouTubeOAuthToken[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedToken, setSelectedToken] = useState<string | undefined>(selectedTokenId);
  const [searchText, setSearchText] = useState('');
  const [associating, setAssociating] = useState(false);
  const [deletingTokenId, setDeletingTokenId] = useState<string | null>(null);

  useEffect(() => {
    loadTokens();
  }, []);

  useEffect(() => {
    // Load currently associated token if any
    if (streamId && !selectedTokenId) {
      loadStreamToken();
    }
  }, [streamId]);

  // Refresh token list when parent signals token expiry
  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      loadTokens();
    }
  }, [refreshTrigger]);

  const loadTokens = async () => {
    setLoading(true);
    try {
      const response = await authAxios({
        method: 'GET',
        url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/oauth-tokens`
      });
      const convertedTokens = await makeKeyInLowerCaseHelper(response.data.tokens);
      setTokens(convertedTokens as YouTubeOAuthToken[]);
    } catch (error) {
      console.error('Error loading OAuth tokens:', error);
      message.error('Failed to load YouTube channels');
    } finally {
      setLoading(false);
    }
  };

  const loadStreamToken = async () => {
    try {
      const response = await authAxios({
        method: 'GET',
        url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/${streamId}/token`
      });
      
      if (response.data.token) {
        const convertedToken = await makeKeyInLowerCaseHelper([response.data.token]);
        setSelectedToken(convertedToken[0].id);
      }
    } catch (error) {
      // No token associated yet, which is fine
      console.log('No token associated with stream yet');
    }
  };

  const handleTokenChange = (e: RadioChangeEvent) => {
    setSelectedToken(e.target.value);
  };

  const handleAssociateToken = async () => {
    if (!selectedToken) {
      message.warning('Please select a YouTube channel');
      return;
    }

    setAssociating(true);
    try {
      await authAxios({
        method: 'POST',
        url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/${streamId}/associate-token`,
        data: {
          token_id: selectedToken,
          user_id: userId
        }
      });

      const token = tokens.find(t => t.id === selectedToken);
      if (token) {
        onTokenSelected(selectedToken, {
          id: token.youtube_channel_id,
          title: token.youtube_channel_title
        });
        message.success('YouTube channel associated successfully');
      }
    } catch (error) {
      console.error('Error associating token:', error);
      message.error('Failed to associate YouTube channel');
    } finally {
      setAssociating(false);
    }
  };

  const handleConnectNew = async () => {
    try {
      const response = await authAxios({
        method: 'GET',
        url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/auth`,
        params: { user_id: userId }
      });
      
      // Open OAuth URL in new window
      window.open(response.data.authUrl, '_blank');
      message.info('Please complete the YouTube authorization in the new window');
      
      // Reload tokens after some time
      setTimeout(() => {
        loadTokens();
      }, 30000);
      
      onConnectNew();
    } catch (error) {
      console.error('Error connecting to YouTube:', error);
      message.error('Failed to connect to YouTube');
    }
  };

  const handleDeleteToken = async (tokenId: string) => {
    setDeletingTokenId(tokenId);
    try {
      // First, dissociate the token from the stream if it's currently associated with this stream
      if (selectedTokenId === tokenId) {
        try {
          await authAxios({
            method: 'DELETE',
            url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/${streamId}/dissociate-token`
          });
        } catch (dissociateError) {
          // Log but continue with deletion even if dissociation fails
          console.log('Error dissociating token from stream:', dissociateError);
        }
      }

      // Delete the OAuth token
      await authAxios({
        method: 'DELETE',
        url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/oauth-tokens/${tokenId}`
      });

      // Remove token from local state
      setTokens(tokens.filter(t => t.id !== tokenId));
      
      // If the deleted token was selected, clear selection
      if (selectedToken === tokenId) {
        setSelectedToken(undefined);
      }

      message.success('YouTube channel deleted successfully');
    } catch (error: unknown) {
      console.error('Error deleting YouTube channel:', error);
      const errorResponse = error as { 
        response?: { 
          status?: number;
          statusText?: string;
          data?: { error?: string; message?: string } 
        }; 
        message?: string 
      };
      const errorMessage = errorResponse?.response?.data?.error || 
                          errorResponse?.response?.data?.message || 
                          errorResponse?.message || 
                          'Failed to delete YouTube channel';
      console.error('Full error details:', {
        status: errorResponse?.response?.status,
        statusText: errorResponse?.response?.statusText,
        data: errorResponse?.response?.data,
        url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/oauth-tokens/${tokenId}`
      });
      message.error(errorMessage);
    } finally {
      setDeletingTokenId(null);
    }
  };

  const filteredTokens = tokens.filter(token => {
    if (!searchText) return true;
    return (
      token.youtube_channel_title.toLowerCase().includes(searchText.toLowerCase()) ||
      token.youtube_channel_id.toLowerCase().includes(searchText.toLowerCase())
    );
  });

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString || dateString.trim() === '') {
      return 'N/A';
    }
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'N/A';
      }
      return date.toLocaleString();
    } catch (error) {
      return 'N/A';
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Card>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div>
          <Title level={5}>
            <YoutubeOutlined style={{ color: '#ff0000', marginRight: '8px' }} />
            Select YouTube Channel
          </Title>
          <Text type="secondary">
            Choose an existing connected YouTube channel or connect a new one
          </Text>
        </div>

        {tokens.length > 0 && (
          <Input
            placeholder="Search channels..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
          />
        )}

        {filteredTokens.length === 0 && !loading ? (
          <Empty
            description={
              tokens.length === 0 
                ? "No YouTube channels connected yet"
                : "No channels match your search"
            }
          >
            {tokens.length === 0 && (
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={handleConnectNew}
              >
                Connect YouTube Channel
              </Button>
            )}
          </Empty>
        ) : (
          <>
            <Radio.Group 
              onChange={handleTokenChange} 
              value={selectedToken}
              style={{ width: '100%' }}
            >
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                {filteredTokens.map((token) => (
                  <Card 
                    key={token.id}
                    size="small"
                    style={{
                      cursor: 'pointer',
                      border: selectedToken === token.id ? '2px solid #1890ff' : '1px solid #d9d9d9'
                    }}
                    onClick={() => setSelectedToken(token.id)}
                  >
                    <Radio value={token.id} style={{ width: '100%' }}>
                      <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <Text strong style={{ fontSize: '16px' }}>
                              {token.youtube_channel_title}
                            </Text>
                            {selectedToken === token.id && (
                              <Tag color="blue" icon={<CheckCircleOutlined />} style={{ marginLeft: '8px' }}>
                                Selected
                              </Tag>
                            )}
                          </div>
                          <Popconfirm
                            title="Delete YouTube Channel"
                            description="Are you sure you want to delete this YouTube channel connection? This action cannot be undone."
                            onConfirm={() => handleDeleteToken(token.id)}
                            okText="Yes, Delete"
                            cancelText="Cancel"
                            okButtonProps={{ danger: true }}
                          >
                            <Button 
                              type="text" 
                              size="small" 
                              danger
                              icon={<DeleteOutlined />}
                              loading={deletingTokenId === token.id}
                              onClick={(e) => e.stopPropagation()}
                            >
                              Delete
                            </Button>
                          </Popconfirm>
                        </div>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          Channel ID: {token.youtube_channel_id}
                        </Text>
                        <Space size="small">
                          <Text type="secondary" style={{ fontSize: '11px' }}>
                            Last used: {formatDate(token.last_used_at)}
                          </Text>
                          {token.expires_at && (
                            <Text type="secondary" style={{ fontSize: '11px' }}>
                              • Expires: {formatDate(token.expires_at)}
                            </Text>
                          )}
                        </Space>
                      </Space>
                    </Radio>
                  </Card>
                ))}
              </Space>
            </Radio.Group>

            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Button 
                icon={<PlusOutlined />}
                onClick={handleConnectNew}
              >
                Connect New Channel
              </Button>
              <Button 
                type="primary"
                onClick={handleAssociateToken}
                disabled={!selectedToken || selectedToken === selectedTokenId}
                loading={associating}
              >
                Use Selected Channel
              </Button>
            </Space>
          </>
        )}

        {selectedTokenId && (
          <Alert
            message="Channel already associated"
            description="This stream already has a YouTube channel associated. You can change it by selecting a different channel above."
            type="info"
            showIcon
          />
        )}
      </Space>
    </Card>
  );
};

export default ChannelSelector;

